/**
 * Quiz Session Runtime — create → hydrate → complete/abandon.
 *
 * Only `create` may assemble/shuffle. F5 / tab restore must `hydrate` by sessionId.
 * Dexie (`examSessionRepository`) is the in-progress adapter, not Attempt evidence.
 *
 * Incomplete-session policy (launch-layer only via prepareQuizRuntimeLaunch):
 * 1. Choice Training → prompt continue / new round on Exam Space
 * 2. Writing → silent_continue same sessionId (no re-draw)
 * 3. Mock → prompt continue / abandon+restart on Exam Space
 * QuizPage must not re-prompt; it only hydrate(sessionId) or create.
 * Create must pin the assembled paper only after the Dexie session row exists —
 * never set pinned paper mid-boot (that used to re-trigger assemble/shuffle).
 */
import { buildPlatformScope, type PlatformScope } from '@examflow/core';
import {
  examSessionRepository,
  type ActiveSession,
  type StoredUserAnswer,
} from '../../data/local';

export const QUIZ_SESSION_ID_PARAM = 'sessionId';

export type QuizSessionMode = 'exam' | 'practice';

/** Launch intent fingerprint for Training / diagnosis (no sessionId). */
export type PracticeLaunchFingerprint = {
  examType: string;
  languageId: string | null;
  sectionId: string | null;
  difficultyFilter: string | null;
  taskNumber: number | null;
  topicId: string | null;
  intensity: string | null;
  questionCount: number | 'all' | null;
};

export type CreatePracticeSessionInput = {
  fingerprint: PracticeLaunchFingerprint;
  questionIds: string[];
  bankIds?: string[];
  platformScope?: PlatformScope | null;
};

export type CreateExamSessionInput = {
  examType: string;
  languageId?: string | null;
  questionIds: string[];
  remainingTimeMs: number;
  bankIds?: string[];
  platformScope?: PlatformScope | null;
  sectionBoundaries?: ActiveSession['sectionBoundaries'];
};

export function readQuizSessionId(search: URLSearchParams): string | null {
  const id = search.get(QUIZ_SESSION_ID_PARAM)?.trim();
  return id || null;
}

export function withQuizSessionId(search: URLSearchParams, sessionId: string): URLSearchParams {
  const next = new URLSearchParams(search);
  next.set(QUIZ_SESSION_ID_PARAM, sessionId);
  return next;
}

export function withoutQuizSessionId(search: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(search);
  next.delete(QUIZ_SESSION_ID_PARAM);
  return next;
}

/**
 * Incomplete Training disposition when a matching session exists without URL sessionId.
 * Launch layer (`prepareQuizRuntimeLaunch`): show_prompt → Exam Space modal; silent_continue → open with sessionId.
 * QuizPage deep-link fallback: show_prompt → abandon then create (never modal); silent_continue → hydrate.
 */
export type IncompletePracticeDisposition = 'show_prompt' | 'silent_continue';

export function hasResumableWritingProgress(input: {
  userAnswers: Array<{ answer?: unknown }>;
  draftEssays: Iterable<string>;
}): boolean {
  if (input.userAnswers.some((answer) => String(answer.answer ?? '').trim().length > 0)) {
    return true;
  }
  for (const essay of input.draftEssays) {
    if (essay.trim().length > 0) return true;
  }
  return false;
}

export function resolveIncompletePracticeDisposition(input: {
  skillId: string | null | undefined;
}): IncompletePracticeDisposition {
  if (input.skillId?.trim() !== 'writing') return 'show_prompt';
  return 'silent_continue';
}

function normalizeNullableString(value: string | null | undefined): string | null {
  const trimmed = value?.trim() || null;
  return trimmed || null;
}

function sameNullableNumber(a: number | null | undefined, b: number | null | undefined): boolean {
  return (a ?? null) === (b ?? null);
}

export function matchesPracticeLaunch(
  session: ActiveSession,
  fingerprint: PracticeLaunchFingerprint
): boolean {
  if (session.mode !== 'practice') return false;
  if (session.examType !== fingerprint.examType) return false;

  const sessionLanguage =
    normalizeNullableString(session.languageId) ||
    normalizeNullableString(session.platformScope?.languageId);
  const intentLanguage = normalizeNullableString(fingerprint.languageId);
  if (intentLanguage && sessionLanguage && sessionLanguage !== intentLanguage) {
    return false;
  }

  if (normalizeNullableString(session.sectionId) !== normalizeNullableString(fingerprint.sectionId)) {
    return false;
  }
  if (
    normalizeNullableString(session.difficultyFilter) !==
    normalizeNullableString(fingerprint.difficultyFilter)
  ) {
    return false;
  }
  if (!sameNullableNumber(session.taskNumber, fingerprint.taskNumber)) {
    return false;
  }
  if (normalizeNullableString(session.topicId) !== normalizeNullableString(fingerprint.topicId)) {
    return false;
  }
  if (
    normalizeNullableString(session.intensity) !== normalizeNullableString(fingerprint.intensity)
  ) {
    return false;
  }

  const sessionCount = session.questionCountTarget ?? null;
  const intentCount = fingerprint.questionCount;
  if (intentCount === 'all') {
    if (sessionCount != null && sessionCount !== 'all') return false;
  } else if (intentCount != null) {
    if (sessionCount !== intentCount) return false;
  }

  return true;
}

/** Hydrate: restore frozen questionIds + progress. Never assemble. */
export async function hydrateQuizSession(sessionId: string): Promise<ActiveSession | null> {
  return examSessionRepository.getSessionById(sessionId);
}

/**
 * Create practice session — sole Training assemble/persist boundary.
 * Caller must pass already-assembled questionIds (from practicePaperAssembly).
 */
export async function createPracticeSession(
  input: CreatePracticeSessionInput
): Promise<ActiveSession> {
  const { fingerprint, questionIds, bankIds } = input;
  const platformScope =
    input.platformScope ??
    buildPlatformScope({
      examType: fingerprint.examType,
      languageId: fingerprint.languageId,
    });
  const id = examSessionRepository.generateId();
  const now = Date.now();
  const session: ActiveSession = {
    id,
    examType: fingerprint.examType,
    languageId: platformScope?.languageId ?? fingerprint.languageId,
    locale: platformScope?.locale ?? null,
    ttsLocale: platformScope?.ttsLocale ?? null,
    platformScope,
    mode: 'practice',
    questionIds,
    userAnswers: [],
    currentIndex: 0,
    remainingTime: 0,
    startedAt: now,
    updatedAt: now,
    bankIds,
    sectionId: fingerprint.sectionId ?? undefined,
    difficultyFilter: fingerprint.difficultyFilter ?? undefined,
    taskNumber: fingerprint.taskNumber,
    topicId: fingerprint.topicId,
    intensity: fingerprint.intensity,
    questionCountTarget: fingerprint.questionCount,
  };
  await examSessionRepository.saveActiveSession(session);
  return session;
}

/** Create mock exam session — sole Mock buildPaper persist boundary. */
export async function createExamSession(input: CreateExamSessionInput): Promise<ActiveSession> {
  const platformScope =
    input.platformScope ??
    buildPlatformScope({
      examType: input.examType,
      languageId: input.languageId,
    });
  const id = examSessionRepository.generateId();
  const now = Date.now();
  const session: ActiveSession = {
    id,
    examType: input.examType,
    languageId: platformScope?.languageId ?? input.languageId ?? null,
    locale: platformScope?.locale ?? null,
    ttsLocale: platformScope?.ttsLocale ?? null,
    platformScope,
    mode: 'exam',
    questionIds: input.questionIds,
    userAnswers: [],
    currentIndex: 0,
    remainingTime: input.remainingTimeMs,
    startedAt: now,
    updatedAt: now,
    bankIds: input.bankIds,
    sectionBoundaries: input.sectionBoundaries,
  };
  await examSessionRepository.saveActiveSession(session);
  return session;
}

export async function findIncompletePracticeSession(
  fingerprint: PracticeLaunchFingerprint
): Promise<ActiveSession | null> {
  const session = await examSessionRepository.getActiveSession({
    examType: fingerprint.examType,
    languageId: fingerprint.languageId,
    mode: 'practice',
    sectionId: fingerprint.sectionId,
  });
  if (!session || session.questionIds.length === 0) return null;
  if (!matchesPracticeLaunch(session, fingerprint)) return null;
  return session;
}

export async function findIncompleteExamSession(lookup: {
  examType: string;
  languageId?: string | null;
}): Promise<ActiveSession | null> {
  const session = await examSessionRepository.getActiveSession({
    examType: lookup.examType,
    languageId: lookup.languageId,
    mode: 'exam',
  });
  if (!session || session.questionIds.length === 0) return null;
  return session;
}

export async function updateQuizSessionProgress(
  sessionId: string,
  updates: {
    userAnswers: StoredUserAnswer[];
    currentIndex: number;
    remainingTime?: number;
  }
): Promise<void> {
  await examSessionRepository.updateActiveSession(sessionId, {
    userAnswers: updates.userAnswers,
    currentIndex: updates.currentIndex,
    ...(updates.remainingTime != null ? { remainingTime: updates.remainingTime } : {}),
  });
}

/** Complete: clear in-progress session after submit / finish. */
export async function completeQuizSession(sessionId: string): Promise<void> {
  await examSessionRepository.deleteActiveSession(sessionId);
}

/** Abandon: discard unfinished session to allow a fresh create. */
export async function abandonQuizSession(sessionId: string): Promise<void> {
  await examSessionRepository.deleteActiveSession(sessionId);
}
